/**
 * ShipSchedule — RC7-DASHBOARD-025.
 *
 * Port-organized Ship Schedule + Voyage Port navigator.
 *
 * IA (ratified 2026-02-19):
 *   · Groups operational times BY PORT — never a flat event stream.
 *   · Sea days do NOT produce port cards.
 *   · Embarkation, Port Call, Disembarkation are the three group types.
 *   · Each group is clickable → opens the canonical destination guide
 *     (`destination-guide:<canonical_subject_id>`). Never a second
 *     detail implementation.
 *   · Voyage-aware chrome: completed / current / next / upcoming.
 *
 * Governance:
 *   · CRUISE-OPS-002 · All-Aboard Semantics — arrive ≠ all_aboard ≠
 *     depart. Missing values omit the line cleanly (no dash / TBD).
 *   · CRUISE-OPS-001 · Embarkation timing — sourced from voyage-root
 *     fields (embark_boarding_window_*, embark_recommended_arrival_time).
 *   · ONE OWNER → MANY CONSUMERS — no Dashboard-only persistence.
 *
 * Time vocabulary (traveller-facing, whitelisted):
 *   Embarkation: "Recommended arrival", "Boarding window", "Ship departs"
 *   Port Call:   "Ship arrives", "All aboard", "Ship departs"
 *                (+ "Guests can go ashore" — reserved for a future
 *                 authoritative source field; NOT emitted today.)
 *   Disembarkation: "Ship arrives"
 */
import React from "react";
import { Clock, MapPin, Anchor, Ship, ChevronRight, Eye, Sparkles, Utensils } from "lucide-react";
import UpcomingWeather from "@/components/cruise-home/UpcomingWeather";

/**
 * Format an "HH:MM" ship-local string into a friendly display
 * (e.g. "8:00 AM"). Returns `null` when the value is missing so the
 * caller can OMIT the line cleanly (never a dash or TBD).
 */
function _fmtHhmm(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${mm} ${ampm}`;
}

function _fmtWindow(start, end) {
  const a = _fmtHhmm(start);
  const b = _fmtHhmm(end);
  if (a && b) return `${a}–${b}`;
  return a || b || null;
}

/** Port-day status derived from `phase` + `signals` (voyage-aware). */
function _computeStatus(pc, phase, signals) {
  const currentId  = phase?.current_port?.id;
  const currentDayN = phase?.day_number;
  const nextPortId  = phase?.next_port?.id;
  // RC7-DASHBOARD-026 · A port_id can appear on both embark and
  // disembark rows (round-trip voyages). Disambiguate using the
  // voyage-day of the next port_call so only the correct row lights up.
  const nextDay    = signals?.next_port_day;
  const pcDay      = pc.day;

  if (currentId && pc.port_id === currentId) return "current";
  if (nextPortId && pc.port_id === nextPortId && (nextDay == null || pcDay === nextDay)) {
    return "next";
  }
  if (typeof currentDayN === "number" && typeof pcDay === "number") {
    if (pcDay < currentDayN) return "completed";
    return "upcoming";
  }
  return "upcoming";
}

const STATUS_CHIP = {
  current:   { label: "Currently in", ring: "ring-emerald-300", tint: "bg-emerald-50 text-emerald-800", dot: "bg-emerald-500" },
  next:      { label: "Next port",    ring: "ring-amber-300",   tint: "bg-amber-50 text-amber-800",     dot: "bg-amber-500"   },
  completed: { label: "Completed",    ring: "ring-slate-200",   tint: "bg-slate-100 text-slate-500",    dot: "bg-slate-400"   },
  upcoming:  { label: "Upcoming",     ring: "ring-slate-200",   tint: "bg-white text-slate-500",        dot: "bg-slate-300"   },
};

function TimeLine({ label, value, testId }) {
  if (!value) return null;
  return (
    <li className="flex items-baseline gap-2 py-0.5" data-testid={testId}>
      <span className="text-slate-500 font-body text-[12.5px] shrink-0">{label}</span>
      <span className="text-slate-300">·</span>
      <span className="text-slate-800 font-body text-[13px] font-semibold tabular-nums">
        {value}
      </span>
    </li>
  );
}

function PortGroup({ pc, kind, status, voyage, weatherUnit, onOpenDestination }) {
  const chip = STATUS_CHIP[status] || STATUS_CHIP.upcoming;
  const port = pc.port || {};
  const portName = port.name || pc.port_name_extracted || "Port";
  const country  = port.country;
  // RC7-DASHBOARD-028 · Prefer the linked destination subject id for
  // deep-linking into the Destination Guide (the guide endpoint expects
  // `subject_type: destination`). Fall back to the port's own canonical
  // subject id so legacy consumers still resolve, even if the destination
  // guide 404s until an editorial destination is written.
  const destinationRouteId = port.destination_subject_id || port.canonical_subject_id || null;
  const clickable = !!destinationRouteId && !!onOpenDestination;
  const discovery = port.discovery || null;

  // Build the timeline strictly from what exists — never invent.
  const lines = [];
  if (kind === "embark") {
    const rec = _fmtHhmm(voyage?.embark_recommended_arrival_time);
    const win = _fmtWindow(voyage?.embark_boarding_window_start, voyage?.embark_boarding_window_end);
    const dep = _fmtHhmm(pc.depart_time);
    if (rec) lines.push({ label: "Recommended arrival", value: rec });
    if (win) lines.push({ label: "Boarding window",     value: win });
    if (dep) lines.push({ label: "Ship departs",         value: dep });
  } else if (kind === "disembark") {
    const arr = _fmtHhmm(pc.arrive_time);
    if (arr) lines.push({ label: "Ship arrives",        value: arr });
  } else {
    // port call
    const arr = _fmtHhmm(pc.arrive_time);
    const aa  = _fmtHhmm(pc.all_aboard_time);
    const dep = _fmtHhmm(pc.depart_time);
    if (arr) lines.push({ label: "Ship arrives", value: arr });
    if (aa)  lines.push({ label: "All aboard",   value: aa });
    if (dep) lines.push({ label: "Ship departs", value: dep });
  }

  const KindIcon = kind === "embark" ? Ship : kind === "disembark" ? Anchor : MapPin;
  const kindLabel = kind === "embark" ? "Embarkation"
                  : kind === "disembark" ? "Disembarkation"
                  : "Port call";

  const clickProps = clickable
    ? {
        role: "button",
        tabIndex: 0,
        onClick: () => onOpenDestination(destinationRouteId, pc),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenDestination(destinationRouteId, pc);
          }
        },
      }
    : {};

  return (
    <div
      className={`ring-1 ${chip.ring} rounded-xl bg-white overflow-hidden transition-shadow ${
        clickable ? "hover:shadow-md cursor-pointer" : ""
      } ${status === "next" ? "shadow-sm" : ""}`}
      data-testid={`ship-schedule-port-${pc.port_id || pc.day}`}
      data-port-status={status}
      data-port-kind={kind}
      {...clickProps}
    >
      <div className="px-4 py-3 flex items-start gap-3">
        <div className={`mt-1 shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${status === "next" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
          <KindIcon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-black tracking-[0.14em] uppercase ${chip.tint}`}
              data-testid="ship-schedule-status-chip"
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${chip.dot}`} aria-hidden />
              {chip.label}
            </span>
            <span className="text-[10.5px] text-slate-400 font-semibold uppercase tracking-[0.14em]">
              {kindLabel}
            </span>
            {/* RC7-DASHBOARD-031 · Per-card Explore action retired.
                The entire card is a click target (see `clickProps`)
                and the section-level helper text under the Ship
                Schedule heading tells travellers how to open the
                Destination Guide. A single trailing chevron marks
                the card as interactive when it is. */}
            {clickable && (
              <ChevronRight
                className="ml-auto w-4 h-4 text-slate-300 shrink-0"
                aria-hidden
                data-testid={`ship-schedule-open-hint-${pc.port_id || pc.day}`}
              />
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 min-w-0">
            <div className="min-w-0 flex-1">
              <div
                className="font-display text-[16px] font-semibold text-slate-900 truncate"
                data-testid="ship-schedule-port-name"
              >
                {portName}
                {country && <span className="text-slate-400 font-normal">, {country}</span>}
              </div>
            </div>
            {port.coords && (
              <UpcomingWeather
                portId={pc.port_id}
                lat={port.coords.lat}
                lng={port.coords.lng}
                unit={weatherUnit}
                size="prominent"
              />
            )}
          </div>
          {lines.length > 0 && (
            <ul className="mt-2" data-testid="ship-schedule-times">
              {lines.map((l) => (
                <TimeLine key={l.label} label={l.label} value={l.value} testId={`ship-schedule-time-${l.label.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </ul>
          )}
          {/* RC7-DASHBOARD-028 · Voyage Ports Discovery Rail.
              Three-line teaser (see / do / taste) sourced from the
              existing canonical destination knowledge (`ckb_subjects`
              of subject_type `destination`) — never manufactured.
              Rendered only when at least one line has content, only
              on port-day cards (skipped for embark/disembark to keep
              those focused on operational timing), and always inside
              the same clickable envelope that opens the Destination
              Guide. */}
          {kind === "port" && discovery && (
            <div
              className="mt-2 pt-2 border-t border-dashed border-slate-200"
              data-testid="ship-schedule-discovery-rail"
              aria-label="Discovery preview"
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
                {discovery.see && (
                  <span className="inline-flex items-center gap-1 min-w-0" data-testid="ship-schedule-discovery-see">
                    <Eye className="w-3 h-3 text-emerald-600 shrink-0" aria-hidden />
                    <span className="text-slate-700 truncate max-w-[260px]">{discovery.see}</span>
                  </span>
                )}
                {discovery.do && (
                  <span className="inline-flex items-center gap-1 min-w-0" data-testid="ship-schedule-discovery-do">
                    <Sparkles className="w-3 h-3 text-amber-600 shrink-0" aria-hidden />
                    <span className="text-slate-700 truncate max-w-[260px]">{discovery.do}</span>
                  </span>
                )}
                {discovery.taste && (
                  <span className="inline-flex items-center gap-1 min-w-0" data-testid="ship-schedule-discovery-taste">
                    <Utensils className="w-3 h-3 text-rose-600 shrink-0" aria-hidden />
                    <span className="text-slate-700 truncate max-w-[260px]">{discovery.taste}</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object}  props
 * @param {object}  props.voyage        Full voyage record (embark_* fields)
 * @param {Array}   props.itinerary     `voyage.itinerary[]` (sea days included; filtered internally)
 * @param {object}  props.phase         traveler-context phase (`current_port`, `next_port`, `day_number`)
 * @param {object}  props.signals       traveler-context signals (`next_port_day` used to disambiguate round-trips)
 * @param {string}  props.weatherUnit
 * @param {(subjectId:string, pc:object) => void} props.onOpenDestination
 */
export default function ShipSchedule({ voyage, itinerary, phase, signals, weatherUnit, onOpenDestination }) {
  // Port groups only — sea days are intentionally excluded per PO
  // directive (RC7-DASHBOARD-025 §5). Ordering follows the voyage's
  // day sequence exactly (ownership is `cruises.itinerary[]`).
  const groups = (itinerary || []).filter((pc) => pc && pc.kind !== "sea_day");
  if (groups.length === 0) return null;

  return (
    <div data-testid="ship-schedule">
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-1.5"
        data-testid="ship-schedule-heading"
      >
        <Clock className="w-3 h-3" /> Ship Schedule
      </div>
      {/* RC7-DASHBOARD-031 · Single section-level instruction that
          replaces the retired per-card Explore buttons. Helper-text
          weight — not a button, not a CTA. */}
      <p
        className="mb-3 font-body text-[12px] text-slate-500"
        data-testid="ship-schedule-helper"
      >
        Select a port to explore its destination guide.
      </p>
      <div className="space-y-3">
        {groups.map((pc) => (
          <PortGroup
            key={`${pc.day}-${pc.port_id || pc.kind}`}
            pc={pc}
            kind={pc.kind || "port"}
            status={_computeStatus(pc, phase, signals)}
            voyage={voyage}
            weatherUnit={weatherUnit}
            onOpenDestination={onOpenDestination}
          />
        ))}
      </div>
    </div>
  );
}

// Named exports for test isolation.
export { PortGroup, _fmtHhmm, _fmtWindow, _computeStatus };
